import { c as _c } from "react/compiler-runtime";
import type { PayloadForEventType, ReviewRunKind, ReviewRunStatus } from '@/lib/realtime/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { countTimedOutPendingRuns, reconcileReviewRuns } from './review-run-optimistic';
import { useRealtime } from '@/lib/hooks/use-realtime';
import { buildRealtimePullRequestId, REALTIME_KEYS } from '@/lib/realtime/types';
export type ReviewRun = {
  id: string;
  reviewRunId: string;
  traceId: string;
  status: ReviewRunStatus;
  runKind: ReviewRunKind;
  ruleId?: string;
  ruleTitle?: string;
  violationCount: number;
  createdAt: string;
  updatedAt: string;
  isPendingOptimistic?: boolean;
  optimisticRequestedAt?: string;
  progressPercentage?: number;
  progressMessage?: string;
  filesProcessed?: number;
  totalFiles?: number;
};
export const reviewRunsQueryKey = (owner: string, repo: string, number: string) => ['reviewRuns', owner, repo, number] as const;
type ReviewStatusEvent = PayloadForEventType<'REVIEW_STATUS_UPDATE'>;
const REVIEW_RUNS_POLL_INTERVAL_MS = 10_000;
export function inferReviewRunKind({
  runKind,
  ruleId,
  fallbackRunKind
}: {
  runKind?: ReviewRunKind;
  ruleId?: string;
  fallbackRunKind?: ReviewRunKind;
}): ReviewRunKind {
  if (runKind) {
    return runKind;
  }
  if (ruleId) {
    return 'rule';
  }
  return fallbackRunKind ?? 'general';
}

/**
 * Fetches persisted run statuses and normalizes partial payloads
 * so UI logic can always rely on required `runKind` and `updatedAt`.
 */
export function getReviewRunsEndpoint(owner: string, repo: string, number: string) {
  return `/api/v2/pull-requests/${owner}/${repo}/${number}/review-statuses`;
}
export function shouldPollReviewRuns(reviewRuns: ReviewRun[] | undefined) {
  return (reviewRuns ?? []).some(run => run.status === 'pending' || run.status === 'running' || run.isPendingOptimistic === true);
}
const fetchReviewStatuses = async (owner: string, repo: string, number: string) => {
  const response = await fetch(getReviewRunsEndpoint(owner, repo, number));
  if (!response.ok) {
    throw new Error('Failed to fetch review statuses');
  }
  const data = (await response.json()) as ReviewRun[];
  return data.map(run => ({
    ...run,
    runKind: inferReviewRunKind({
      runKind: run.runKind,
      ruleId: run.ruleId
    }),
    updatedAt: run.updatedAt ?? run.createdAt
  }));
};

/**
 * Merges a realtime status event into cached review runs while preserving the
 * first-seen `createdAt` and updating `updatedAt` on each transition.
 */
export function mergeReviewRunFromRealtimeEvent(oldData: ReviewRun[] = [], event: ReviewStatusEvent): ReviewRun[] {
  const existingIndex = oldData.findIndex(status => status.reviewRunId === event.reviewRunId);
  const existingRun = existingIndex >= 0 ? oldData[existingIndex] : undefined;
  const inferredRunKind = inferReviewRunKind({
    runKind: event.runKind,
    ruleId: event.ruleId,
    fallbackRunKind: existingRun?.runKind
  });
  const createdAt = existingRun?.createdAt ?? event.createdAt;
  const updatedAt = event.updatedAt ?? event.createdAt;

  // Keep progress fields only while the run is active; clear on terminal transitions
  const isRunning = event.status === 'running';
  const progressPercentage = isRunning ? event.progressPercentage ?? existingRun?.progressPercentage : undefined;
  const progressMessage = isRunning ? event.progressMessage ?? existingRun?.progressMessage : undefined;
  const filesProcessed = isRunning ? event.filesProcessed ?? existingRun?.filesProcessed : undefined;
  const totalFiles = isRunning ? event.totalFiles ?? existingRun?.totalFiles : undefined;
  const newReviewRun: ReviewRun = {
    id: event.reviewRunId,
    reviewRunId: event.reviewRunId,
    traceId: event.traceId,
    status: event.status,
    runKind: inferredRunKind,
    ruleId: event.ruleId ?? existingRun?.ruleId,
    ruleTitle: event.ruleTitle ?? existingRun?.ruleTitle,
    violationCount: event.violationCount,
    createdAt,
    updatedAt,
    progressPercentage,
    progressMessage,
    filesProcessed,
    totalFiles
  };
  if (existingIndex >= 0) {
    const newReviewRuns = [...oldData];
    newReviewRuns[existingIndex] = newReviewRun;
    return newReviewRuns;
  }
  return [...oldData, newReviewRun];
}

/**
 * A pull request's review runs, polled while one is active. `useReviewRuns` also applies the
 * review-status channel's updates; a caller that already refetches runs on the pull request's
 * `cubic_review` updates reads this alone, because a second subscriber to that channel on the
 * shared socket shares it, and whichever unmounts first closes it for the other.
 */
export function useReviewRunsQuery(owner, repo, number) {
  const $ = _c(22);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = reviewRunsQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== queryClient || $[7] !== queryKey || $[8] !== repo) {
    t1 = async () => {
      const fetchedRuns = await fetchReviewStatuses(owner, repo, number);
      const cachedRuns = queryClient.getQueryData(queryKey) ?? [];
      return reconcileReviewRuns(fetchedRuns, cachedRuns, Date.now());
    };
    $[4] = number;
    $[5] = owner;
    $[6] = queryClient;
    $[7] = queryKey;
    $[8] = repo;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = Boolean(owner && repo && number);
  let t3;
  if ($[10] !== queryKey || $[11] !== t1 || $[12] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      placeholderData: _temp,
      refetchInterval: _temp2,
      enabled: t2,
      refetchOnWindowFocus: false
    };
    $[10] = queryKey;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const {
    data: reviewRuns,
    isPending: isLoading,
    error
  } = useQuery(t3);
  let t4;
  if ($[14] !== reviewRuns) {
    t4 = reviewRuns ?? [];
    $[14] = reviewRuns;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const normalizedRuns = t4;
  const timedOutPendingCount = countTimedOutPendingRuns(normalizedRuns);
  const t5 = timedOutPendingCount > 0;
  let t6;
  if ($[16] !== error || $[17] !== isLoading || $[18] !== normalizedRuns || $[19] !== t5 || $[20] !== timedOutPendingCount) {
    t6 = {
      reviewRuns: normalizedRuns,
      isLoading,
      error,
      hasTimedOutPendingRuns: t5,
      timedOutPendingCount
    };
    $[16] = error;
    $[17] = isLoading;
    $[18] = normalizedRuns;
    $[19] = t5;
    $[20] = timedOutPendingCount;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}

/** A pull request's review runs, kept current by the review-status channel. */
function _temp2(query) {
  return shouldPollReviewRuns(query.state.data as ReviewRun[] | undefined) ? REVIEW_RUNS_POLL_INTERVAL_MS : false;
}
function _temp() {}
export function useReviewRuns(owner, repo, number) {
  const $ = _c(18);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = buildRealtimePullRequestId(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const prId = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = reviewRunsQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const queryKey = t1;
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== queryClient || $[11] !== queryKey || $[12] !== repo) {
    t2 = event => {
      if (event.pr.owner === owner && event.pr.repo === repo && event.pr.number.toString() === number) {
        queryClient.setQueryData(queryKey, t3 => {
          const oldData = t3 === undefined ? [] : t3;
          const mergedWithRealtime = mergeReviewRunFromRealtimeEvent(oldData, event);
          return reconcileReviewRuns(mergedWithRealtime, oldData, Date.now());
        });
      }
    };
    $[8] = number;
    $[9] = owner;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = repo;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const t3 = Boolean(owner && repo && number);
  let t4;
  if ($[14] !== prId || $[15] !== t2 || $[16] !== t3) {
    t4 = {
      eventKey: REALTIME_KEYS.REVIEW_STATUS_UPDATE,
      id: prId,
      onEvent: t2,
      enabled: t3
    };
    $[14] = prId;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  useRealtime(t4);
  return useReviewRunsQuery(owner, repo, number);
}